import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

/**
 * Canal de exercício de direitos do titular (LGPD Art. 18).
 * Pública — titular pode não ter conta, pode ter sido só um signatário.
 * SLA legal: 15 dias úteis.
 */

const DSR_TYPES = [
  { value: 'ACCESS',        label: 'Acessar meus dados',           hint: 'Saber quais dados meus a PAKT tem' },
  { value: 'RECTIFICATION', label: 'Corrigir dados',                hint: 'Atualizar dados desatualizados ou errados' },
  { value: 'DELETION',      label: 'Apagar meus dados',             hint: 'Eliminação de dados desnecessários ou excessivos' },
  { value: 'ANONYMIZATION', label: 'Anonimizar dados',              hint: 'Remover identificadores diretos' },
  { value: 'PORTABILITY',   label: 'Portabilidade',                 hint: 'Receber meus dados em formato estruturado' },
  { value: 'INFO',          label: 'Quem recebe meus dados',        hint: 'Lista de operadores/sub-operadores' },
  { value: 'REVOKE',        label: 'Revogar consentimento',         hint: 'Cancelar consentimento dado anteriormente' },
] as const

export default function ExerciseRights() {
  const [type, setType] = useState<typeof DSR_TYPES[number]['value']>('ACCESS')
  const [fullName, setFullName] = useState('')
  const [email, setEmail] = useState('')
  const [cpf, setCpf] = useState('')
  const [description, setDescription] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState<{ id: string; slaInDays: number } | null>(null)
  const [error, setError] = useState('')

  const [contact, setContact] = useState<{ dpoEmail: string | null; dpoName: string | null; privacyEmail: string | null } | null>(null)

  useEffect(() => {
    fetch('/api/legal/platform')
      .then(r => r.ok ? r.json() : null)
      .then(setContact)
      .catch(() => null)
  }, [])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (description.trim().length < 10) {
      setError('Descreva seu pedido com pelo menos 10 caracteres.')
      return
    }
    setSubmitting(true)
    setError('')
    try {
      const res = await fetch('/api/dsr', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type, fullName: fullName.trim(), email: email.trim(),
          cpf: cpf.trim() || undefined,
          description: description.trim(),
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.message || body.error || `HTTP ${res.status}`)
      }
      const data = await res.json()
      setSubmitted({ id: data.id, slaInDays: data.slaInDays })
    } catch (err: any) {
      setError(err.message || 'Falha ao enviar pedido')
    } finally {
      setSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-xl border border-gray-200 p-8 max-w-md text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Pedido recebido</h1>
          <p className="text-gray-600 mb-2">
            Protocolo: <code className="font-mono text-sm bg-gray-100 px-2 py-0.5 rounded">{submitted.id.slice(-12)}</code>
          </p>
          <p className="text-gray-600 mb-6">
            Vamos responder no e-mail informado em até <strong>{submitted.slaInDays} dias úteis</strong>
            {' '}(prazo legal da LGPD Art. 19).
          </p>
          <Link to="/" className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm">
            Voltar ao site
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-indigo-600">PAKT</Link>
          <Link to="/privacidade" className="text-sm text-indigo-600 hover:text-indigo-800">Política de Privacidade</Link>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Exercer direitos do titular</h1>
        <p className="text-gray-600 mb-6">
          Como titular de dados pessoais tratados pela PAKT, você tem direitos garantidos pela
          LGPD Art. 18. Use este canal pra fazer pedidos formais. Resposta em até 15 dias úteis.
        </p>

        {contact && (contact.dpoEmail || contact.privacyEmail) && (
          <div className="mb-6 bg-blue-50 border border-blue-200 p-3 rounded text-sm">
            <strong className="text-blue-900">Contato direto:</strong>
            {contact.dpoName && contact.dpoEmail && (
              <div className="text-blue-900 mt-1">
                Encarregado (DPO): {contact.dpoName} — <a href={`mailto:${contact.dpoEmail}`} className="underline">{contact.dpoEmail}</a>
              </div>
            )}
            {contact.privacyEmail && (
              <div className="text-blue-900 mt-1">
                Canal de privacidade: <a href={`mailto:${contact.privacyEmail}`} className="underline">{contact.privacyEmail}</a>
              </div>
            )}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Tipo de pedido</label>
            <div className="space-y-2">
              {DSR_TYPES.map(t => (
                <label key={t.value} className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="radio"
                    name="type"
                    value={t.value}
                    checked={type === t.value}
                    onChange={() => setType(t.value)}
                    className="mt-1"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">{t.label}</div>
                    <div className="text-xs text-gray-500">{t.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">Nome completo *</label>
              <input value={fullName} onChange={e => setFullName(e.target.value)} required minLength={2} maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 block mb-1">E-mail pra resposta *</label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} required maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">CPF (opcional, ajuda a localizar dados)</label>
            <input value={cpf} onChange={e => setCpf(e.target.value)} maxLength={20} placeholder="000.000.000-00"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 block mb-1">Descrição do pedido *</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={5} required minLength={10} maxLength={5000}
              placeholder="Ex: Quero saber quais documentos meu CPF assinou pela PAKT nos últimos 6 meses."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500" />
            <p className="text-xs text-gray-500 mt-1">{description.length}/5000</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded text-sm">{error}</div>
          )}

          <button type="submit" disabled={submitting}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-semibold disabled:opacity-50">
            {submitting ? 'Enviando...' : 'Enviar pedido'}
          </button>

          <p className="text-xs text-gray-500 mt-4">
            Seus dados neste formulário são tratados pela PAKT TECNOLOGIA LTDA com a finalidade
            única de processar seu pedido (LGPD Art. 7º, II — cumprimento de obrigação legal).
            Retenção: 5 anos após resolução do pedido.
          </p>
        </form>
      </main>
    </div>
  )
}
